// Phase 4 — Approvals page (Prepare → Review → Approve workflow)
import { useEffect, useState, useCallback } from "react";
import BackToDashBar from "@/components/BackToDashBar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { CheckCheck, X, Eye, Plus, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import RecordTrail, { OwnerCell } from "@/components/RecordTrail";
import PendingApprovalsTab from "@/components/PendingApprovalsTab";
import TxPipelinePanel from "@/components/TxPipelinePanel";

const KINDS = ["report", "reconciliation", "payroll", "manual_je", "compliance"];

function statusBadge(s) {
  const map = {
    submitted: "bg-amber-100 text-amber-700",
    reviewed: "bg-sky-100 text-sky-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
  };
  return <Badge className={`${map[s] || "bg-slate-100"} border-0`}>{s}</Badge>;
}

export default function ApprovalsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const canSubmit = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_ACCOUNTANT, ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER].includes(role);
  const canReview = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER].includes(role);
  const canApprove = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_FINANCE_MANAGER].includes(role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pipeline");
  const [drawer, setDrawer] = useState(null);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "report", title: "", period_label: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals");
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => {
    if (tab === "pending") return ["submitted", "reviewed"].includes(it.status);
    if (tab === "approved") return it.status === "approved";
    if (tab === "rejected") return it.status === "rejected";
    return true;
  });

  async function submit() {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      await api.post("/approvals", form);
      toast.success("Approval request submitted");
      setOpen(false); setForm({ kind: "report", title: "", period_label: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function act(id, action) {
    try {
      const { data } = await api.post(`/approvals/${id}/${action}`, { comment });
      setDrawer(data); setComment("");
      toast.success(`Marked ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-5" data-testid="approvals-page">
      <BackToDashBar />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold">Approvals</h2>
          <p className="text-sm text-slate-500 mt-1">Prepare → Review → Approve flow for reports, reconciliations, payroll and journal entries.</p>
        </div>
        {canSubmit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="new-approval-btn">
                <Plus className="h-4 w-4 mr-1.5" /> New approval request
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New approval request</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Kind</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="approval-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="approval-title" /></div>
                <div><Label>Period</Label><Input placeholder="e.g. May 2026" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} /></div>
                <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="submit-approval-btn">Submit</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="approval-tabs">
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Transaction Pipeline</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          {(user?.role === ROLES.SYSTEM_ADMIN || user?.full_access === true) && (
            <TabsTrigger value="admin_pending" data-testid="tab-admin-pending">
              Pending Admin Approval
            </TabsTrigger>
          )}
        </TabsList>
        {tab === "pipeline" && (
          <TabsContent value="pipeline" className="mt-4">
            <TxPipelinePanel />
          </TabsContent>
        )}
        {tab === "admin_pending" && (
          <TabsContent value="admin_pending" className="mt-4">
            <PendingApprovalsTab />
          </TabsContent>
        )}
        {tab !== "admin_pending" && tab !== "pipeline" && (
          <TabsContent value={tab} className="mt-4">
          {loading
            ? <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
            : filtered.length === 0
              ? <Card><CardContent className="py-10 text-center text-slate-400">No items.</CardContent></Card>
              : <div className="grid gap-3 md:grid-cols-2">
                  {filtered.map((it) => (
                    <Card key={it.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDrawer(it)} data-testid={`approval-card-${it.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-slate-900">{it.title}</h3>
                          {statusBadge(it.status)}
                        </div>
                        <p className="text-xs text-slate-500 mb-2"><Badge variant="secondary">{it.kind}</Badge> · {it.period_label || "—"}</p>
                        <p className="text-xs text-slate-500">Prepared by {it.prepared_by_email} · {it.prepared_at?.slice(0, 10)}</p>
                        <OwnerCell created_by_email={it.prepared_by_email} created_at={it.prepared_at} updated_by_email={it.approved_by_email || it.reviewed_by_email} updated_at={it.approved_at || it.reviewed_at} />
                        <Button variant="ghost" size="sm" className="mt-2 text-[#4FA3D1] px-0"><Eye className="h-3 w-3 mr-1" /> View <ChevronRight className="h-3 w-3 ml-1" /></Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
          }
        </TabsContent>
        )}
      </Tabs>

      <Sheet open={!!drawer} onOpenChange={(o) => { if (!o) { setDrawer(null); setComment(""); } }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {drawer && <>
            <SheetHeader><SheetTitle>{drawer.title}</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Kind</span><div><Badge variant="secondary">{drawer.kind}</Badge></div></div>
                <div><span className="text-slate-500">Status</span><div>{statusBadge(drawer.status)}</div></div>
                <div><span className="text-slate-500">Period</span><div>{drawer.period_label || "—"}</div></div>
                <div><span className="text-slate-500">Prepared by</span><div>{drawer.prepared_by_email}</div></div>
                <div className="col-span-2"><span className="text-slate-500">Submitted</span><div>{drawer.prepared_at?.slice(0, 19).replace("T", " ")}</div></div>
                {drawer.notes && <div className="col-span-2"><span className="text-slate-500">Notes</span><p className="mt-1 text-slate-700">{drawer.notes}</p></div>}
              </div>

              {(canReview || canApprove) && !["approved", "rejected"].includes(drawer.status) && (
                <div className="space-y-2 rounded-md border border-slate-200 p-3">
                  <Label className="text-xs">Comment</Label>
                  <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="approval-comment" />
                  <div className="flex flex-wrap gap-2">
                    {canReview && drawer.status === "submitted" && (
                      <Button size="sm" variant="outline" onClick={() => act(drawer.id, "review")} data-testid="review-btn">
                        <Eye className="h-4 w-4 mr-1.5" /> Mark reviewed
                      </Button>
                    )}
                    {canApprove && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(drawer.id, "approve")} data-testid="approve-btn">
                          <CheckCheck className="h-4 w-4 mr-1.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => act(drawer.id, "reject")} data-testid="reject-btn">
                          <X className="h-4 w-4 mr-1.5" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase mb-2">History</p>
                <ol className="space-y-2">
                  {(drawer.history || []).map((h, i) => (
                    <li key={i} className="rounded-md bg-slate-50 p-3 text-sm">
                      <p className="font-semibold capitalize">{h.action}</p>
                      <p className="text-xs text-slate-500">{h.at?.slice(0, 19).replace("T", " ")} — {h.user_email}</p>
                      {h.comment && <p className="mt-1 text-slate-700">{h.comment}</p>}
                    </li>
                  ))}
                </ol>
              </div>
              <RecordTrail entity_type="approval" entity_id={drawer.id} record={{...drawer, created_by_email: drawer.prepared_by_email, created_at: drawer.prepared_at}} />
            </div>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
